/**
 * Which primary/secondary objectives to show during profiling,
 * given main motivation (purpose) and experience level.
 *
 * Must stay in sync with Backend/src/config/profilingFocusCatalog.js
 *
 * Attribute IDs match tbl_attributes from sql/create_normalized_profiling_tables.js
 *
 * Competition SMART options are limited to objectives that map to a competency
 * block (see FOCUS_TO_COMPETENCY_BLOCKS). Non-CB options (endurance, self-confidence,
 * Other, etc.) are excluded from the competition catalog.
 */
import { FOCUS_OTHER_ATTRIBUTE_ID } from "./focusAttributes";
import { LEISURE_BEGINNER_VISIBLE_FOCUS_IDS } from "./leisureSmartPresets";

export const PURPOSE_HEALTH = 1;
export const PURPOSE_RECREATIONAL = 2;
export const PURPOSE_COMPETITION = 3;

export const EXPERIENCE_BEGINNER = 1;
export const EXPERIENCE_INTERMEDIATE = 2;
export const EXPERIENCE_ADVANCED = 3;

/** Technical objectives (strokes, serve, ball touch / effects). */
export const TECHNICAL_IDS = [1, 7, 9, 10];

/**
 * Competition-related objectives that map to a competency block:
 * footwork → Lateral connection, match strategy → Game scheme,
 * adaptability → Playstyles, strength & power → FH/BH/Effects,
 * competitive mindset → Game scheme.
 */
export const COMPETITION_RELATED_IDS = [8, 11, 12, 15, 18];

/**
 * Focus attribute_id → competency block(s) used for exercise recommendations.
 * Names must match tbl_blocks_compentency.cb_name_en / cb_name_fr.
 */
export const FOCUS_TO_COMPETENCY_BLOCKS = {
  1: [{ en: "Fundamentals", fr: "Fondamentaux" }],
  7: [{ en: "First contact with the ball", fr: "Premier contact avec la balle" }],
  9: [
    { en: "Forehand", fr: "Coup droit" },
    { en: "Backhand", fr: "Revers" },
  ],
  /** Ball touch (FR: Touche de balle) → Effects */
  10: [{ en: "The effects", fr: "Les effets" }],
  8: [{ en: "Lateral connection", fr: "Liaison latéral" }],
  11: [{ en: "Game scheme", fr: "Schéma de jeu" }],
  12: [{ en: "Playstyles", fr: "Styles de jeu" }],
  15: [
    { en: "Forehand", fr: "Coup droit" },
    { en: "Backhand", fr: "Revers" },
    { en: "The effects", fr: "Les effets" },
  ],
  18: [{ en: "Game scheme", fr: "Schéma de jeu" }],
};

/** Leisure amusement + relaxation (fun, social, stress relief). */
export const LEISURE_AMUSEMENT_RELAXATION_IDS = [3, 4, 19];

/**
 * Light competition for leisure beginners / intermediates —
 * fundamentals plus a competitive spark, not advanced match tactics.
 */
export const LEISURE_LIGHT_COMPETITION_IDS = [1, 7, 8, 10];

/** Health catalog (unchanged). */
export const HEALTH_IDS = [1, 2, 3, 4, 5, 6, 19, 20, 21, 22];

export function purposeToFocusCategoryId(purposeId) {
  const id = Number(purposeId);
  if (id === PURPOSE_HEALTH) return 1;
  if (id === PURPOSE_RECREATIONAL) return 2;
  return 3;
}

/**
 * Categories to fetch so the visible list can be assembled.
 * Leisure also loads the competitive catalog for light-competition options.
 * Health also loads leisure options (fun / social / unwind / basics / keep ball).
 */
export function focusCategoryIdsToLoad(purposeId) {
  if (Number(purposeId) === PURPOSE_RECREATIONAL) return [2, 3];
  if (Number(purposeId) === PURPOSE_HEALTH) return [1, 2];
  return [purposeToFocusCategoryId(purposeId)];
}

export function isBeginnerOrIntermediate(experienceLevelId) {
  const id = Number(experienceLevelId);
  if (!Number.isFinite(id) || id <= 0) return true;
  return id === EXPERIENCE_BEGINNER || id === EXPERIENCE_INTERMEDIATE;
}

export function allowedFocusAttributeIds({ purposeId, experienceLevelId } = {}) {
  const purpose = Number(purposeId);
  const other = [FOCUS_OTHER_ATTRIBUTE_ID];

  if (purpose === PURPOSE_COMPETITION) {
    // CB-linked only — no "Other", endurance, self-confidence, etc.
    return [...TECHNICAL_IDS, ...COMPETITION_RELATED_IDS];
  }

  if (purpose === PURPOSE_RECREATIONAL) {
    const leisureCore = [...LEISURE_AMUSEMENT_RELAXATION_IDS, ...other];
    if (isBeginnerOrIntermediate(experienceLevelId)) {
      return [...leisureCore, ...LEISURE_LIGHT_COMPETITION_IDS];
    }
    return leisureCore;
  }

  // Health: SCforH catalog + leisure-style options + Other
  return [
    ...new Set([
      ...HEALTH_IDS,
      ...LEISURE_BEGINNER_VISIBLE_FOCUS_IDS,
      ...other,
    ]),
  ];
}

export function filterFocusAttributes(attributes, ctx = {}) {
  const allowed = new Set(allowedFocusAttributeIds(ctx).map(Number));
  return (attributes || []).filter((a) =>
    allowed.has(Number(a && a.attribute_id))
  );
}

export function collectAttributesByCategory(attributesByCategory, purposeId) {
  const byId = new Map();
  for (const catId of focusCategoryIdsToLoad(purposeId)) {
    for (const attr of (attributesByCategory && attributesByCategory[catId]) || []) {
      if (attr && attr.attribute_id != null) {
        byId.set(Number(attr.attribute_id), attr);
      }
    }
  }
  return [...byId.values()];
}

export function visibleFocusAttributes(attributesByCategory, ctx = {}) {
  const collected = collectAttributesByCategory(
    attributesByCategory,
    ctx.purposeId
  );
  let visible = filterFocusAttributes(collected, ctx);
  if (
    Number(ctx.purposeId) === PURPOSE_RECREATIONAL &&
    isBeginnerOrIntermediate(ctx.experienceLevelId)
  ) {
    const beginnerSet = new Set(LEISURE_BEGINNER_VISIBLE_FOCUS_IDS.map(Number));
    visible = visible.filter((a) => beginnerSet.has(Number(a.attribute_id)));
  }
  if (ctx.excludeAttributeId != null) {
    const exclude = Number(ctx.excludeAttributeId);
    visible = visible.filter((a) => Number(a.attribute_id) !== exclude);
  }
  return visible;
}
